import React from 'react';
import { useSimulationStore } from '@/store/useSimulationStore';
import { useUIStore, NavTab } from '@/store/useUIStore';
import { formatTokens, formatCurrency, formatPercent } from '@/utils/formatters';
import { budgetRatio } from '@/utils/formatters';

const TABS: { id: NavTab; label: string }[] = [
  { id: 'live', label: 'LIVE' },
  { id: 'stores', label: 'BOUTIQUES' },
  { id: 'pipeline', label: 'PIPELINE' },
  { id: 'economy', label: 'ÉCONOMIE' },
  { id: 'rules', label: 'RÈGLES' },
];

export const TopBar: React.FC = () => {
  const metrics = useSimulationStore(s => s.metrics);
  const events = useSimulationStore(s => s.events);
  const { activeTab, setActiveTab } = useUIStore();

  const budgetPct = budgetRatio(metrics.tokensUsedToday, metrics.totalTokensBudget);
  const unread = events.filter(e => !e.read).length;
  const isCritical = budgetPct > 0.85;

  return (
    <header className="flex flex-col border-b border-bg-border bg-bg-panel select-none">
      {/* ── Métriques économiques ─────────────────────────────── */}
      <div className="flex items-center gap-4 md:gap-6 px-3 md:px-4 py-2 border-b border-bg-border overflow-x-auto scrollbar-thin whitespace-nowrap">
        {/* Logo */}
        <div className="flex items-center gap-2 mr-4 flex-shrink-0">
          <span className="font-pixel text-neon-cyan text-xs tracking-wider" style={{ textShadow: '0 0 8px #00E5FF' }}>
            MISSION
          </span>
          <span className="font-pixel text-neon-gold text-xs tracking-wider" style={{ textShadow: '0 0 8px #FFD700' }}>
            CTRL
          </span>
        </div>

        <Metric label="TOKENS RESTANTS" value={formatTokens(metrics.totalTokensBudget - metrics.tokensUsedToday)} color={isCritical ? '#FF1744' : '#00E5FF'} />
        <BudgetBar used={metrics.tokensUsedToday} total={metrics.totalTokensBudget} critical={isCritical} />
        <Metric label="CA DU JOUR" value={formatCurrency(metrics.totalRevenue)} color="#FFD700" />
        <Metric label="MARGE" value={formatCurrency(metrics.totalMargin)} sub={`${formatPercent(metrics.totalRevenue > 0 ? metrics.totalMargin / metrics.totalRevenue : 0)}`} color="#00E676" />
        <Metric label="COMMANDES" value={String(metrics.totalOrders)} color="#E040FB" />

        <div className="flex-1" />

        {/* Indicateurs système */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {unread > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 rounded border border-neon-amber bg-neon-amber/10">
              <span className="text-neon-amber font-mono text-xs font-bold">{unread}</span>
              <span className="text-neon-amber font-mono text-xs">ALERTS</span>
            </div>
          )}
          <div className="flex items-center gap-1 text-neon-green">
            <div className="w-2 h-2 rounded-full bg-neon-green mc-glow" />
            <span className="font-mono text-xs">{metrics.agentsOnline} ONLINE</span>
          </div>
          {metrics.pendingValidations > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 rounded border border-neon-amber bg-neon-amber/10 animate-pulse">
              <span className="text-neon-amber font-mono text-xs">⚠ {metrics.pendingValidations} VALIDATION{metrics.pendingValidations > 1 ? 'S' : ''}</span>
            </div>
          )}
          <span className="font-pixel text-xs text-gray-600">DAY {metrics.dayNumber}</span>
        </div>
      </div>

      {/* ── Navigation ───────────────────────────────────────── */}
      <nav className="flex items-center gap-0 px-2 md:px-4 overflow-x-auto scrollbar-thin">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={[
              'px-4 py-2 font-pixel text-xs tracking-wider transition-all duration-150 border-b-2',
              activeTab === tab.id
                ? 'text-neon-cyan border-neon-cyan'
                : 'text-gray-500 border-transparent hover:text-gray-300 hover:border-gray-600',
            ].join(' ')}
          >
            {tab.label}
            {tab.id === 'pipeline' && metrics.pendingValidations > 0 && (
              <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-neon-amber align-middle" />
            )}
          </button>
        ))}
      </nav>
    </header>
  );
};

const Metric: React.FC<{ label: string; value: string; sub?: string; color: string }> = ({ label, value, sub, color }) => (
  <div className="flex flex-col flex-shrink-0">
    <span className="font-mono text-xs text-gray-600 uppercase tracking-widest">{label}</span>
    <span className="font-mono text-sm font-bold" style={{ color, textShadow: `0 0 8px ${color}60` }}>
      {value}
    </span>
    {sub && <span className="font-mono text-xs text-gray-500">{sub}</span>}
  </div>
);

const BudgetBar: React.FC<{ used: number; total: number; critical: boolean }> = ({ used, total, critical }) => {
  const pct = Math.min(1, used / total);
  return (
    <div className="flex flex-col gap-0.5 flex-shrink-0">
      <span className="font-mono text-xs text-gray-600">BUDGET TOKENS</span>
      <div className="flex items-center gap-2">
        <div className="w-24 h-2 bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${pct * 100}%`,
              background: critical
                ? 'linear-gradient(90deg, #FF6D00, #FF1744)'
                : 'linear-gradient(90deg, #00E5FF, #2979FF)',
              boxShadow: critical ? '0 0 6px #FF1744' : '0 0 6px #00E5FF',
            }}
          />
        </div>
        <span className="font-mono text-xs" style={{ color: critical ? '#FF1744' : '#4a5568' }}>
          {formatTokens(used)}/{formatTokens(total)}
        </span>
      </div>
    </div>
  );
};
